import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faSpinner } from "@fortawesome/free-solid-svg-icons";
import ActionPlaceholder from "./ActionPlaceholder";
import { IMAGE_ACCEPT, FRIENDLY_IMAGE_LIST, validateMediaFile } from "../../utils/mediaTypes";

export default function PhotoUploadTray({
  addLabel,
  uploadFn,
  token,
  max = 10,
  resetKey,
  tileClass = "",
  onUploaded,
  onError,
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [stagedFiles, setStagedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [doneCount, setDoneCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const stagedRef = useRef(stagedFiles);
  stagedRef.current = stagedFiles;

  // A batch settles asynchronously, so the reset effect and the unmount cleanup both
  // need the live flag and the live callback rather than the ones from their own render.
  const uploadingRef = useRef(false);
  const pendingResetRef = useRef(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Drop everything staged. Files reach photo_albums only through Upload, so anything
  // still here was never uploaded: say so instead of discarding it silently.
  const discardStaged = (warn) => {
    const dropped = stagedRef.current;
    dropped.forEach((item) => {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    });
    stagedRef.current = [];
    setStagedFiles([]);
    setDoneCount(0);
    setTotalCount(0);

    if (warn && dropped.length > 0 && onErrorRef.current) {
      onErrorRef.current(
        t("{{count}} selected photos were not uploaded and have been discarded.", {
          count: dropped.length,
        })
      );
    }
  };

  // Revoke object URLs on unmount (leaving edit mode, saving, navigating away)
  useEffect(() => {
    return () => {
      const dropped = stagedRef.current;
      dropped.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
      if (dropped.length > 0 && onErrorRef.current) {
        onErrorRef.current(
          t("{{count}} selected photos were not uploaded and have been discarded.", {
            count: dropped.length,
          })
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear staged files when resetKey changes. Never force isUploading back to false here:
  // that would unlock the tray while a batch is still running and let a second batch
  // overwrite the first one's photo_albums update. Defer the reset until the batch settles.
  useEffect(() => {
    if (uploadingRef.current) {
      pendingResetRef.current = true;
      return;
    }
    discardStaged(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const handleFileSelect = (e) => {
    const incoming = Array.from(e.target.files || []);
    if (incoming.length === 0) return;

    const invalidNames = [];
    const validFiles = [];

    incoming.forEach((file) => {
      const err = validateMediaFile(file, "image");
      if (err) {
        invalidNames.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    // onError is a single slot on both galleries (one message, one modal), so a second
    // call would overwrite the first. Collect every problem and report them together.
    const problems = [];

    if (invalidNames.length > 0) {
      problems.push(
        t(
          "These files are not supported and were skipped: {{names}}. Please use one of: {{formats}}.",
          { names: invalidNames.join(", "), formats: FRIENDLY_IMAGE_LIST }
        )
      );
    }

    if (validFiles.length > 0) {
      // Cap and create previews outside the state updater: updaters must stay pure,
      // otherwise a double invocation leaks one object URL per file.
      const roomLeft = Math.max(max - stagedFiles.length, 0);
      const skipped = validFiles.length - roomLeft;

      if (skipped > 0) {
        problems.push(
          t("You can add up to {{max}} images at a time. {{skipped}} were not added.", {
            max,
            skipped,
          })
        );
      }

      const toStage = validFiles.slice(0, roomLeft).map((file) => ({
        id: Math.random().toString(36).substring(2) + Date.now(),
        file,
        previewUrl: URL.createObjectURL(file),
        status: "idle",
        key: null,
      }));

      if (toStage.length > 0) {
        setStagedFiles((prev) => [...prev, ...toStage]);
      }
    }

    if (problems.length > 0 && onError) {
      onError(problems.join(" "));
    }

    e.target.value = "";
  };

  const removeFile = (id) => {
    if (isUploading) return;
    setStagedFiles((prev) => {
      const item = prev.find((i) => i.id === id);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((i) => i.id !== id);
    });
  };

  const clearAll = () => {
    if (isUploading) return;
    discardStaged(false);
  };

  const handleUpload = async () => {
    if (isUploading) return;

    const itemsToUpload = stagedFiles.filter(
      (item) => item.status === "idle" || item.status === "failed"
    );

    if (itemsToUpload.length === 0) return;

    uploadingRef.current = true;
    setIsUploading(true);
    setDoneCount(0);
    setTotalCount(itemsToUpload.length);

    setStagedFiles((prev) =>
      prev.map((item) =>
        item.status === "idle" || item.status === "failed"
          ? { ...item, status: "uploading" }
          : item
      )
    );

    const uploadResults = await Promise.allSettled(
      itemsToUpload.map(async (item) => {
        try {
          const key = await uploadFn(item.file, token);
          setDoneCount((prev) => prev + 1);
          return { id: item.id, key };
        } catch (err) {
          return { id: item.id, error: err };
        }
      })
    );

    const successfulKeys = [];
    const succeededIds = new Set();
    const failedIds = new Set();

    uploadResults.forEach((res, index) => {
      const targetItem = itemsToUpload[index];
      if (res.status === "fulfilled" && res.value && res.value.key) {
        successfulKeys.push(res.value.key);
        succeededIds.add(targetItem.id);
        if (targetItem.previewUrl) {
          URL.revokeObjectURL(targetItem.previewUrl);
        }
      } else {
        failedIds.add(targetItem.id);
      }
    });

    uploadingRef.current = false;
    setIsUploading(false);

    // onUploaded is the closure from the render the user pressed Upload in, so the keys
    // land in the album they picked even if resetKey changed while the batch ran.
    if (successfulKeys.length > 0 && onUploaded) {
      onUploaded(successfulKeys);
    }

    if (pendingResetRef.current) {
      // resetKey changed mid-batch; the reset was deferred so it could not clobber it.
      pendingResetRef.current = false;
      discardStaged(false);
      // The failed tiles go with it, so do not point at a retry button that is gone.
      if (failedIds.size > 0 && onError) {
        onError(
          t("{{count}} selected photos were not uploaded and have been discarded.", {
            count: failedIds.size,
          })
        );
      }
      return;
    }

    setStagedFiles((prev) =>
      prev
        .filter((item) => !succeededIds.has(item.id))
        .map((item) =>
          failedIds.has(item.id) ? { ...item, status: "failed" } : item
        )
    );

    if (failedIds.size > 0 && onError) {
      onError(
        t("Some images could not be uploaded. Tap upload to try again.")
      );
    }
  };

  return (
    <div className="tw-shrink-0 tw-snap-center tw-flex tw-flex-col tw-gap-3">
      {/* sticky: the tray sits inside the gallery's horizontal scroller, so without it
          the Upload button pans off-screen as soon as the user scrolls the previews */}
      {stagedFiles.length > 0 && (
        <div className="tw-sticky tw-left-0 tw-z-30 tw-self-start tw-flex tw-items-center tw-gap-3 tw-bg-[#1E0039] tw-border tw-border-[#5A3F49]/60 tw-rounded-xl tw-p-2.5 md:tw-p-3">
          <span className="tw-text-[#E2BDC9] tw-text-xs md:tw-text-sm tw-font-bold">
            {isUploading
              ? t("Uploading {{done}} of {{total}}...", {
                  done: doneCount,
                  total: totalCount,
                })
              : t("Ready to upload: {{count}}", {
                  count: stagedFiles.length,
                })}
          </span>
          <div className="tw-flex tw-items-center tw-gap-2">
            {!isUploading && (
              <>
                <button
                  type="button"
                  onClick={handleUpload}
                  className="tw-px-3 md:tw-px-4 tw-py-1.5 tw-bg-[#FF43A7] hover:tw-bg-[#FF00A0] tw-text-white tw-font-bold tw-text-xs tw-uppercase tw-tracking-wider tw-rounded-lg tw-border-none tw-cursor-pointer tw-transition-colors"
                >
                  {t("Upload")}
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  className="tw-px-3 md:tw-px-4 tw-py-1.5 tw-bg-transparent tw-border tw-border-[#5A3F49] hover:tw-bg-white/5 tw-text-[#E2BDC9] tw-font-bold tw-text-xs tw-uppercase tw-tracking-wider tw-rounded-lg tw-cursor-pointer tw-transition-colors"
                >
                  {t("Clear all")}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="tw-flex tw-items-center tw-gap-4 md:tw-gap-6 tw-h-full">
        <input
          type="file"
          accept={IMAGE_ACCEPT}
          multiple
          ref={fileInputRef}
          onChange={handleFileSelect}
          className="tw-hidden"
          disabled={isUploading}
        />

        {stagedFiles.length < max && (
          <div
            className={`tw-shrink-0 tw-snap-center ${tileClass} ${
              isUploading
                ? "tw-opacity-40 tw-pointer-events-none tw-cursor-not-allowed"
                : ""
            }`}
          >
            <ActionPlaceholder
              variant="photo"
              title={addLabel}
              onClick={() => !isUploading && fileInputRef.current?.click()}
            />
          </div>
        )}

        {stagedFiles.map((item) => (
          <div
            key={item.id}
            className={`tw-shrink-0 tw-snap-center tw-relative tw-bg-[#280D41] tw-rounded-xl tw-overflow-hidden tw-shadow-lg ${tileClass}`}
          >
            <img
              src={item.previewUrl}
              alt="Preview"
              className="tw-w-full tw-h-full tw-object-cover"
            />

            {!isUploading && (
              <div className="tw-absolute tw-top-3 tw-right-3 tw-z-20">
                <button
                  type="button"
                  onClick={() => removeFile(item.id)}
                  title={t("Remove")}
                  className="tw-w-8 tw-h-8 tw-bg-black/60 hover:tw-bg-red-500 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-white tw-border-none tw-cursor-pointer tw-transition-colors tw-shadow-lg"
                >
                  <FontAwesomeIcon icon={faXmark} className="tw-text-sm" />
                </button>
              </div>
            )}

            {item.status === "uploading" && (
              <div className="tw-absolute tw-inset-0 tw-bg-[#1E0039]/80 tw-backdrop-blur-sm tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-z-20">
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="tw-text-[#FF43A7] tw-text-2xl tw-animate-spin"
                />
                <span className="tw-text-[#FF43A7] tw-font-bold tw-text-xs tw-uppercase tw-tracking-widest">
                  {t("Uploading...")}
                </span>
              </div>
            )}

            {item.status === "failed" && (
              <div className="tw-absolute tw-bottom-3 tw-left-1/2 tw--translate-x-1/2 tw-z-20">
                <span className="tw-bg-red-500 tw-text-white tw-font-bold tw-text-[10px] tw-uppercase tw-tracking-wider tw-px-2 tw-py-1 tw-rounded tw-shadow">
                  {t("Failed")}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
